import type { RowData } from "@tanstack/react-table";
import {
    FILE_TYPE_ICONS,
    splitFileName,
} from "@design-system/FileTypeIcon/fileType";
import { CellText } from "./CellText";
import { toDisplayText } from "./cellValues";
import { EmptyCellValue } from "./EmptyCellValue";
import { HighlightedText } from "./HighlightedText";
import type { DataGridCellProps } from "./types";
import styles from "./cellContent.module.scss";

interface FileNameCellProps<
    TData extends RowData,
> extends DataGridCellProps<TData> {
    /** Leading file-type icon from the extension. */
    showIcon: boolean;
}

/**
 * File name column cell (`DocumentName`'s look): the name with a muted
 * extension, search highlights on the name, and an optional file-type icon.
 */
export const FileNameCell = <TData extends RowData>({
    context,
    meta,
    showIcon,
}: Readonly<FileNameCellProps<TData>>) => {
    const fileName = toDisplayText(context.getValue());
    if (fileName === null) {
        return <EmptyCellValue empty={meta.empty} />;
    }
    const { name, extension } = splitFileName(fileName);
    const text = (
        <CellText text={fileName} overflow={meta.overflow}>
            <HighlightedText text={name} table={context.table} />
            {extension === null ? null : (
                <span className={styles.suffix}>
                    <HighlightedText
                        text={`.${extension}`}
                        table={context.table}
                    />
                </span>
            )}
        </CellText>
    );
    const FileTypeIcon =
        showIcon && extension !== null
            ? FILE_TYPE_ICONS[extension.toLowerCase()]
            : undefined;
    if (!FileTypeIcon) {
        return text;
    }
    return (
        <span className={styles.inline}>
            <span className={styles.leading}>
                <FileTypeIcon aria-hidden />
            </span>
            {text}
        </span>
    );
};
